// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

import { useState } from 'react'
import { SignIn as SignInIcon, ShieldCheck, UserPlus } from '@phosphor-icons/react'
import { useI18n } from '../i18n'
import * as api from '../api'

type Mode = 'password' | 'mfa'

export function SignIn({ onSignedIn }: { onSignedIn: (a: api.Account) => void }) {
  const { t } = useI18n()
  const [mode, setMode] = useState<Mode>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [mfaToken, setMfaToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ text: string; cls: string } | null>(null)

  const apply = (r: api.LoginOutcome) => {
    switch (r.kind) {
      case 'signed_in':
        onSignedIn(r.account)
        return
      case 'mfa_required':
        // Mật khẩu đúng nhưng chưa đủ. KHÔNG có token và KHÔNG có cookie refresh
        // ở bước này — phiếu chờ là thứ duy nhất ta đang cầm (ADR-0003 §7).
        setMfaToken(r.token)
        setMode('mfa')
        setMsg({ text: r.message, cls: 'wait' })
        return
      case 'unverified':
        setMsg({ text: r.message, cls: 'wait' })
        return
      case 'failed':
        setMsg({ text: r.message, cls: 'err' })
    }
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setMsg(null)
    try {
      apply(
        mode === 'password'
          ? await api.signIn(email.trim(), password)
          : await api.completeMFA(mfaToken, code.trim()),
      )
    } finally {
      setBusy(false)
    }
  }

  const doRegister = async () => {
    setBusy(true)
    setMsg(null)
    try {
      const r = await api.register(email.trim(), email.split('@')[0] ?? 'Z', password)
      // ⚠️ Phản hồi GIỐNG HỆT nhau dù địa chỉ đã có tài khoản hay chưa
      // (ADR-0005 §3). Đừng cố suy ra gì thêm ở đây — người dùng thật biết
      // chuyện gì xảy ra qua bức thư, và đó chính là toàn bộ cơ chế.
      setMsg({ text: r.message, cls: r.ok ? 'ok' : 'err' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="center">
      <form className="card" onSubmit={submit}>
        <h1>{t('ui.auth.heading')}</h1>
        <p className="sub">{t('ui.auth.hint')}</p>

        {mode === 'password' ? (
          <>
            <label htmlFor="email">{t('ui.auth.email')}</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <label htmlFor="password">{t('ui.auth.password')}</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <div className="row" style={{ marginTop: 16 }}>
              <button type="submit" style={{ flex: 1 }} disabled={busy}>
                <SignInIcon size={18} weight="light" />
                {t('ui.auth.sign_in')}
              </button>
              <button type="button" className="sec" style={{ flex: 1 }} disabled={busy} onClick={doRegister}>
                <UserPlus size={18} weight="light" />
                {t('ui.auth.register')}
              </button>
            </div>
          </>
        ) : (
          <>
            <label htmlFor="mfa">{t('ui.auth.mfa_code')}</label>
            <input
              id="mfa"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
            />
            <button className="wide" type="submit" disabled={busy}>
              <ShieldCheck size={18} weight="light" />
              {t('ui.auth.confirm')}
            </button>
          </>
        )}

        {msg && <div className={'msg ' + msg.cls}>{msg.text}</div>}

        {mode === 'password' && (
          <p className="sub" style={{ marginTop: 14, marginBottom: 0 }}>
            <button
              type="button"
              className="linkish"
              disabled={busy || !email.trim()}
              onClick={async () => {
                const r = await api.resendVerification(email.trim())
                setMsg({ text: r.message, cls: 'ok' })
              }}
            >
              {t('ui.verify.resend')}
            </button>
          </p>
        )}
      </form>
    </main>
  )
}
